import React, { useState } from "react";
import {
  Select,
  ErrorMessage,
  SubmitButton,
  Radio,
} from "components/Forms";
import WatchForm from "components/Forms/WatchForm";
import { useFormContext } from "react-hook-form";
import {
  MILLING_TYPES,
  FEED_TYPES,
  SPEED_TYPES,
  MATERIAL_SELECTION,
} from "./constants/constants";
import "./index.css";

const CuttingParameters = (props) => {
  const formCtx = useFormContext();
  const [image, setImage] = useState("assets/images/up-milling.png");

  const speedType = formCtx?.watch("cuttingParameters.speedTypeSelection");
  const feedType = formCtx?.watch("cuttingParameters.feedTypeSelection");
  const millingType = formCtx?.watch("cuttingParameters.millingTypeSelection");

  const millingTypesRadioHandler = (value) => {
    formCtx.setValue("cuttingParameters.millingTypeSelection", value);

    switch (value) {
      case 1:
        setImage("assets/images/up-milling.png");
        break;
      case 2:
        setImage("assets/images/down-milling.png");
        break;
      case 3:
        setImage("assets/images/slotting.png");
        break;
      default:
        setImage("assets/images/up-milling.png");
    }
  };

  const speedTypeRadioHandler = (value) => {
    formCtx.setValue("cuttingParameters.speedTypeSelection", value);

    if (value === 1) {
      formCtx.setValue("cuttingParameters.cuttingSpeed", 0);
    } else {
      formCtx.setValue("cuttingParameters.spindleSpeed", 0);
    }
  };

  const feedTypeRadioHandler = (value) => {
    formCtx.setValue("cuttingParameters.feedTypeSelection", value);

    if (value === 1) {
      formCtx.setValue("cuttingParameters.feedPerTooth", 0);
    } else {
      formCtx.setValue("cuttingParameters.feedRate", 0);
    }
  };

  const handleNumberChange = (name, value, min = 0, max = Infinity) => {
    let parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) {
      parsedValue = 0;
    }

    if (parsedValue < min) {
      parsedValue = min;
    }

    if (parsedValue > max) {
      parsedValue = max;
    }

    formCtx.setValue(name, parsedValue);
  };

  const renderNumberInput = ({
    name,
    label,
    unit,
    min = 0,
    max = Infinity,
    step = "0.1",
    value,
  }) => (
    <div className="input-wrapper d-flex flex-row align-items-center mb-4">
      <div className="d-flex flex-column col-3 align-items-center">
        <label
          className="col-form-label"
          style={{ width: props.windowWidth < 994 && "100px" }}
        >
          {label}
        </label>
      </div>

      <div className="d-flex col-9 align-items-center">
        <div className="d-flex flex-column col-8">
          <input
            type="number"
            step={step}
            min={min}
            max={max}
            value={value ?? 0}
            className="form-control"
            style={{ maxWidth: "220px" }}
            onChange={(e) => handleNumberChange(name, e.target.value, min, max)}
            onBlur={(e) => {
              if (e.target.value === "") {
                formCtx.setValue(name, 0);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          />
          <small className="text-muted mt-1">
            Range: {min} - {max === Infinity ? "No limit" : max} {unit}
          </small>
          <div className="mt-2">
            <ErrorMessage name={name} />
          </div>
        </div>

        <div className="ml-3" style={{ minWidth: "70px", fontWeight: 500 }}>
          {unit}
        </div>
      </div>
    </div>
  );

  return (
    <div className="d-flex flex-column">
      {props.windowWidth <= 994 && (
        <div className="align-self-center col-5 ml-5">
          <img
            style={{
              maxHeight: "350px",
              width: "100%",
              marginTop: props.windowWidth <= 994 ? "10%" : "50%",
            }}
            src={image}
            alt="Milling process"
          />
        </div>
      )}

      <div
        className={
          props.windowWidth <= 994
            ? "d-flex justify-content-center"
            : "d-flex justify-content-start"
        }
      >
        <div
          className={
            props.windowWidth <= 994
              ? "cutting-parameters-input-container col-12 p-0"
              : "cutting-parameters-input-container col-8 pl-4 pr-4"
          }
        >
          <div className="d-flex justify-content-center mt-1 ml-5">
            <h3 className="text-center mb-5">Cutting Parameter Selection</h3>
          </div>

          <div className="inputs">
            <div className="input-wrapper d-flex flex-row align-items-center pb-3">
              <div className="d-flex flex-row col-3">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Milling Type
                </label>
              </div>

              <div
                className="input-wrapper d-flex col-9 justify-content-start align-items-center"
                style={{ marginLeft: props.windowWidth < 994 && "40px" }}
              >
                <div className="d-flex col-12 flex-row">
                  <div className="btn-group-toggle w-100" data-toggle="buttons">
                    <Radio
                      onChange={(value) => millingTypesRadioHandler(value)}
                      name="cuttingParameters.millingTypeSelection"
                      options={MILLING_TYPES}
                    />
                  </div>
                  <ErrorMessage name="cuttingParameters.millingTypeSelection" />
                </div>
              </div>
            </div>

            <WatchForm name="fluteLength">
              {(fluteLength) =>
                renderNumberInput({
                  name: "cuttingParameters.depthOfCut",
                  label: "Depth of Cut (mm):",
                  unit: "mm",
                  min: 0,
                  max: fluteLength || 0,
                  step: "0.1",
                  value: formCtx.watch("cuttingParameters.depthOfCut"),
                })
              }
            </WatchForm>

            <WatchForm name="diameter">
              {(diameter) => {
                const widthValue =
                  millingType === 3
                    ? diameter || 0
                    : formCtx.watch("cuttingParameters.widthOfCut");

                if (millingType === 3) {
                  formCtx.setValue("cuttingParameters.widthOfCut", diameter || 0);
                }

                return renderNumberInput({
                  name: "cuttingParameters.widthOfCut",
                  label: "Width of Cut (mm):",
                  unit: "mm",
                  min: 0,
                  max: diameter || 0,
                  step: "0.1",
                  value: widthValue,
                });
              }}
            </WatchForm>

            <div className="input-wrapper d-flex flex-row align-items-center pb-3">
              <div className="d-flex flex-row col-3">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Speed Type
                </label>
              </div>

              <div
                className="input-wrapper d-flex col-9 justify-content-start align-items-center"
                style={{ marginLeft: props.windowWidth < 994 && "40px" }}
              >
                <div className="d-flex col-12 flex-row">
                  <div className="btn-group-toggle h-50" data-toggle="buttons">
                    <Radio
                      onChange={(value) => speedTypeRadioHandler(value)}
                      name="cuttingParameters.speedTypeSelection"
                      options={SPEED_TYPES}
                    />
                  </div>
                </div>
              </div>
            </div>

            {speedType === 1
              ? renderNumberInput({
                  name: "cuttingParameters.spindleSpeed",
                  label: "Spindle Speed (RPM):",
                  unit: "RPM",
                  min: 0,
                  max: 40000,
                  step: "1",
                  value: formCtx.watch("cuttingParameters.spindleSpeed"),
                })
              : renderNumberInput({
                  name: "cuttingParameters.cuttingSpeed",
                  label: "Cutting Speed (m/min):",
                  unit: "m/min",
                  min: 0,
                  max: 15000,
                  step: "0.1",
                  value: formCtx.watch("cuttingParameters.cuttingSpeed"),
                })}

            <div className="input-wrapper d-flex flex-row align-items-center pb-3">
              <div className="d-flex flex-row col-3">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Feed Type
                </label>
              </div>

              <div
                className="input-wrapper d-flex col-9 justify-content-start align-items-center"
                style={{ marginLeft: props.windowWidth < 994 && "40px" }}
              >
                <div className="d-flex col-12 flex-row">
                  <div className="btn-group-toggle h-50" data-toggle="buttons">
                    <Radio
                      onChange={(value) => feedTypeRadioHandler(value)}
                      name="cuttingParameters.feedTypeSelection"
                      options={FEED_TYPES}
                    />
                  </div>
                </div>
              </div>
            </div>

            {feedType === 1
              ? renderNumberInput({
                  name: "cuttingParameters.feedRate",
                  label: "Feed Rate (mm/min):",
                  unit: "mm/min",
                  min: 0,
                  max: 40000,
                  step: "1",
                  value: formCtx.watch("cuttingParameters.feedRate"),
                })
              : renderNumberInput({
                  name: "cuttingParameters.feedPerTooth",
                  label: "Feed Per Tooth (mm/tooth):",
                  unit: "mm/tooth",
                  min: 0,
                  max: 1,
                  step: "0.1",
                  value: formCtx.watch("cuttingParameters.feedPerTooth"),
                })}

            <div className="d-flex ml-4 justify-content-center">
              <div className="w-50 ml-5 mb-2">
                <ErrorMessage name="cuttingParameters.feedTypeSelection" />
              </div>
            </div>

            <div className="input-wrapper d-flex flex-row align-items-center mb-3">
              <div className="d-flex flex-column col-3 align-items-center">
                <label
                  className="col-form-label"
                  style={{ width: props.windowWidth < 994 && "100px" }}
                >
                  Workpiece Material:
                </label>
              </div>

              <div className="d-flex col-10">
                <Select
                  name="cuttingParameters.materialSelection"
                  options={MATERIAL_SELECTION}
                  className="form-control ml-3 w-75"
                  placeholder="Shape"
                />
              </div>
            </div>
          </div>

          <div
            style={{ marginLeft: "5em" }}
            className="d-flex align-items-center justify-content-center"
          >
            <SubmitButton
              type="submit"
              className="btn btn-info waves-effect waves-light"
              style={{
                width: "200px",
                backgroundColor: "#3398DB",
                color: "#fff",
              }}
              disabled={props.loading}
            >
              Submit
            </SubmitButton>
          </div>
        </div>

        {props.windowWidth > 994 && (
          <div className="col-4">
            <img
              style={{
                maxHeight: "25em",
                marginTop: props.windowWidth <= 994 ? "10%" : "65%",
              }}
              src={image}
              alt="Milling process"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CuttingParameters;